import React, { ReactNode, useState } from 'react';
import { first } from 'lodash';
import { FormattedMessage } from 'react-intl';

import { Inverter } from '../shared';

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';

import { useSelector, useDispatch } from '../store/utils';
import { variablesChange } from '../store/actions';

import { ControlBox } from '../atoms/control-box';
import { Row } from '../atoms/row';
import { InverterWizard } from './inverter-wizard/inverter-wizard';
import { Board } from '../../../core/src/shared/enums';

const defaultModbusAddress = '100.100.1.100';
const defaultModbusPort = 502;

const defaultLegacyModbusConfig = {
  MODBUS_TEST_ADDRESS: defaultModbusAddress,
  MODBUS_TEST_PORT: defaultModbusPort,
};

enum ConnectionType {
  ModbusTCP = 'modbus-tcp',
  RS485 = 'rs485',
}

export const InverterTypeSelect = () => {
  const [wizardOpen, setWizardOpen] = useState(false);

  const [
    board,
    boardRS485,
    boardRS485Host,
    boardRS485Port,
    legacyModbusAddress,
    inverterType,
    inverterConfig,
    legacyInverters,
    ems,
  ] = useSelector(
    (s) =>
      [
        s.variables.BOARD,
        s.variables.BOARD_RS485,
        s.variables.BOARD_RS485_HOST,
        s.variables.BOARD_RS485_PORT,
        s.variables.MODBUS_TEST_ADDRESS,
        s.variables.PV_INVERTER_TYPE,
        s.variables.INVERTER_CONFIG,
        s.variables.LEGACY_INVERTERS,
        s.variables.EMS,
      ] as const
  );

  const selectValue = useSelector((s) => {
    const inverterType = s.variables.PV_INVERTER_TYPE;
    const inverterConfig = s.variables.INVERTER_CONFIG;
    const inverterConfigArray = Object.values(inverterConfig);

    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.Victron
    ) {
      return Inverter.Victron;
    }
    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.SofarSolarRS485
    ) {
      return Inverter.SofarSolarRS485;
    }
    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.DesBatteryContainer
    ) {
      return Inverter.DesBatteryContainer;
    }
    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.Hes
    ) {
      return Inverter.Hes;
    }

    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.GoodWeNext
    ) {
      return Inverter.GoodWeNext;
    }
    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.GoodWeGridNext
    ) {
      return Inverter.GoodWeGridNext;
    }
    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.SolaxMICGrid
    ) {
      return Inverter.SolaxMICGrid;
    }
    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.SolaxMEGAGrid
    ) {
      return Inverter.SolaxMEGAGrid;
    }
    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.SolaxNext
    ) {
      return Inverter.SolaxNext;
    }
    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.SolaxEMS1000
    ) {
      return Inverter.SolaxEMS1000;
    }
    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.SolaxEMS1000PRO
    ) {
      return Inverter.SolaxEMS1000PRO;
    }

    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.Solinteg
    ) {
      return Inverter.Solinteg;
    }
    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.Deye
    ) {
      return Inverter.Deye;
    }

    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.DeyeHV
    ) {
      return Inverter.DeyeHV;
    }

    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.Wattsonic
    ) {
      return Inverter.Wattsonic;
    }

    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.Fronius
    ) {
      return Inverter.Fronius;
    }

    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.FroniusGrid
    ) {
      return Inverter.FroniusGrid;
    }

    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.SmaGrid
    ) {
      return Inverter.SmaGrid;
    }

    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.FroniusVerto
    ) {
      return Inverter.FroniusVerto;
    }
    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.SolarEdgeGrid
    ) {
      return Inverter.SolarEdgeGrid;
    }

    if (
      inverterType === Inverter.Next &&
      inverterConfigArray.length === 1 &&
      inverterConfigArray[0].type === Inverter.Sermatec
    ) {
      return Inverter.Sermatec;
    }

    return inverterType;
  });
  const dispatch = useDispatch();

  const handleChange = (e: SelectChangeEvent<Inverter>) => {
    const newInverterType = e.target.value as Inverter;

    if (newInverterType === Inverter.Victron) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            victron: {
              type: Inverter.Victron,
              address: '100.100.1.100',
              port: 502,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.SofarSolarRS485) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            sofarSolar: { type: Inverter.SofarSolarRS485 },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.DesBatteryContainer) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            desBatteryContainer: {
              type: Inverter.DesBatteryContainer,
              address: '100.100.1.100',
              port: 502,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.Hes) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            hes: { type: Inverter.Hes },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.Solinteg) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            solinteg: {
              type: Inverter.Solinteg,
              address: '100.100.1.100',
              port: 502,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.GoodWeNext) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            goodWe: {
              type: Inverter.GoodWeNext,
              address: '100.100.1.100',
              port: 502,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.SolaxNext) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            solax: {
              type: Inverter.SolaxNext,
              address: '100.100.1.100',
              port: 502,
              master: true,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.SolaxEMS1000) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            solaxEMS1000: {
              type: Inverter.SolaxEMS1000,
              address: '100.100.1.100',
              port: 502,
              inverters: [{}],
              batteryCount: 1,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.SolaxEMS1000PRO) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            solaxEMS1000PRO: {
              type: Inverter.SolaxEMS1000PRO,
              address: '100.100.1.100',
              port: 502,
              inverters: [{}],
              batteryCount: 1,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.Deye) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            deye: {
              type: Inverter.Deye,
              address: '100.100.1.100',
              port: 502,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.DeyeHV) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            deyeHV: {
              type: Inverter.DeyeHV,
              address: '100.100.1.100',
              port: 502,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.GoodWeGridNext) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            goodWeGrid: {
              type: Inverter.GoodWeGridNext,
              address: '100.100.1.100',
              port: 502,
              nominalPower: 10,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.SolaxMICGrid) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            solaxMICGrid: {
              type: Inverter.SolaxMICGrid,
              address: '100.100.1.100',
              port: 502,
              nominalPower: 10,
              maxPowerPercentage: 100,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.SolaxMEGAGrid) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            solaxMEGAGrid: {
              type: Inverter.SolaxMEGAGrid,
              address: '100.100.1.100',
              port: 502,
              master: true,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.Wattsonic) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            wattsonic: {
              type: Inverter.Wattsonic,
              address: '100.100.1.100',
              port: 502,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.Fronius) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            fronius: {
              type: Inverter.Fronius,
              address: '100.100.1.100',
              port: 502,
              master: true,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.FroniusGrid) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            froniusGrid: {
              type: Inverter.FroniusGrid,
              address: '100.100.1.100',
              port: 502,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.SmaGrid) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            smaGrid: {
              type: Inverter.SmaGrid,
              address: '100.100.1.100',
              port: 502,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.FroniusVerto) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            froniusVerto: {
              type: Inverter.FroniusVerto,
              address: '100.100.1.100',
              port: 502,
              master: true,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.SolarEdgeGrid) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            solarEdgeGrid: {
              type: Inverter.SolarEdgeGrid,
              address: '100.100.1.100',
              port: 1502,
              nominalPower: 10,
            },
          },
        })
      );

      return;
    }

    if (newInverterType === Inverter.Sermatec) {
      dispatch(
        variablesChange({
          PV_INVERTER_TYPE: Inverter.Next,
          INVERTER_CONFIG: {
            sermatec: {
              type: Inverter.Sermatec,
              address: '100.100.1.100',
              port: 502,
            },
          },
        })
      );

      return;
    }

    dispatch(
      variablesChange({
        PV_INVERTER_TYPE: newInverterType,
        INVERTER_CONFIG: {},
        ...defaultLegacyModbusConfig,
      })
    );
  };

  const connectionTypeVisible = ![
    Inverter.SofarSolarRS485,
    Inverter.Hes,
    Inverter.Next,
    Inverter.None,
    Inverter.SolaxGrid,
    Inverter.GoodWeGrid,
  ].includes(selectValue);

  const rs485Supported =
    boardRS485 &&
    [
      Inverter.Solax,
      Inverter.SolaxMICGrid,
      Inverter.SolaxMEGAGrid,
      Inverter.GoodWe,
      Inverter.GoodWeNext,
      Inverter.SolaxNext,
      Inverter.GoodWeGridNext,
      Inverter.Solinteg,
      Inverter.Deye,
      Inverter.DeyeHV,
      Inverter.Wattsonic,
      Inverter.Fronius,
      Inverter.FroniusGrid,
      Inverter.FroniusVerto,
      Inverter.Sermatec,
      Inverter.DesBatteryContainer,
    ].includes(selectValue);

  const showLegacyInverters =
    legacyInverters ||
    selectValue === Inverter.GoodWeGrid ||
    selectValue === Inverter.SolaxGrid ||
    selectValue === Inverter.Sofarsolar;
  [
    Inverter.Solax,
    Inverter.SolaxMICGrid,
    Inverter.SolaxMEGAGrid,
    Inverter.GoodWe,
    Inverter.GoodWeNext,
    Inverter.SolaxNext,
    Inverter.GoodWeGridNext,
    Inverter.Solinteg,
    Inverter.Deye,
    Inverter.Wattsonic,
    Inverter.Hes,
    Inverter.Fronius,
    Inverter.FroniusGrid,
    Inverter.FroniusVerto,
    Inverter.SolarEdgeGrid,
    Inverter.Sermatec,
  ].includes(selectValue);
  [
    Inverter.Solax,
    Inverter.SolaxMICGrid,
    Inverter.SolaxMEGAGrid,
    Inverter.GoodWe,
    Inverter.GoodWeNext,
    Inverter.SolaxNext,
    Inverter.GoodWeGridNext,
    Inverter.Solinteg,
    Inverter.Deye,
    Inverter.Wattsonic,
    Inverter.Hes,
    Inverter.Fronius,
    Inverter.FroniusGrid,
    Inverter.FroniusVerto,
    Inverter.SolarEdgeGrid,
    Inverter.Sermatec,
  ].includes(selectValue);

  const inverterTypeSelect = (
    <FormControl>
      <InputLabel id="inverter-type-label">
        <FormattedMessage
          id="settings.service.inverter_type.label"
          defaultMessage="Střídač"
        />
      </InputLabel>
      <Select
        value={selectValue}
        onChange={handleChange}
        labelId="inverter-type-label"
        label={
          <FormattedMessage
            id="settings.service.inverter_type.label"
            defaultMessage="Střídač"
          />
        }
      >
        <MenuItem value={Inverter.GoodWe} key={Inverter.GoodWe}>
          <FormattedMessage id="inverter.good_we" defaultMessage="GoodWe" />
        </MenuItem>
        <MenuItem value={Inverter.GoodWeNext} key={Inverter.GoodWeNext}>
          <FormattedMessage
            id="inverter.good_we_next"
            defaultMessage="GoodWeNext"
          />
        </MenuItem>
        <MenuItem value={Inverter.SolaxNext} key={Inverter.SolaxNext}>
          <FormattedMessage
            id="inverter.solax_next"
            defaultMessage="SolaxNext"
          />
        </MenuItem>
        {ems && (
          <>
            <MenuItem value={Inverter.SolaxEMS1000} key={Inverter.SolaxEMS1000}>
              <FormattedMessage
                id="inverter.solax_ems1000"
                defaultMessage="Solax EMS1000"
              />
            </MenuItem>
            <MenuItem
              value={Inverter.SolaxEMS1000PRO}
              key={Inverter.SolaxEMS1000PRO}
            >
              <FormattedMessage
                id="inverter.solax_ems1000pro"
                defaultMessage="Solax EMS1000PRO"
              />
            </MenuItem>
          </>
        )}
        <MenuItem value={Inverter.GoodWeGridNext} key={Inverter.GoodWeGridNext}>
          <FormattedMessage
            id="inverter.good_we_grid_next"
            defaultMessage="GoodWe síťový"
          />
        </MenuItem>
        <MenuItem value={Inverter.Solax} key={Inverter.Solax}>
          <FormattedMessage id="inverter.solax" defaultMessage="Solax" />
        </MenuItem>
        <MenuItem value={Inverter.SolaxMEGAGrid} key={Inverter.SolaxMEGAGrid}>
          <FormattedMessage
            id="inverter.solax_mega_grid"
            defaultMessage="Solax MEGA/FORTH síťový"
          />
        </MenuItem>

        <MenuItem value={Inverter.SolaxMICGrid} key={Inverter.SolaxMICGrid}>
          <FormattedMessage
            id="inverter.solax_mic_grid"
            defaultMessage="Solax MIC síťový"
          />
        </MenuItem>
        {showLegacyInverters && (
          <MenuItem value={Inverter.Sofarsolar} key={Inverter.Sofarsolar}>
            <FormattedMessage
              id="inverter.sofar_solar"
              defaultMessage="Sofarsolar"
            />
          </MenuItem>
        )}
        {showLegacyInverters && (
          <MenuItem value={Inverter.SolaxGrid} key={Inverter.SolaxGrid}>
            <FormattedMessage
              id="inverter.solax_grid"
              defaultMessage="Solax síťový"
            />
          </MenuItem>
        )}
        {showLegacyInverters && (
          <MenuItem value={Inverter.GoodWeGrid} key={Inverter.GoodWeGrid}>
            <FormattedMessage
              id="inverter.goodwe_grid"
              defaultMessage="Goodwe síťový"
            />
          </MenuItem>
        )}
        <MenuItem value={Inverter.Victron} key={Inverter.Victron}>
          <FormattedMessage id="inverter.victron" defaultMessage="Victron" />
        </MenuItem>
        <MenuItem value={Inverter.Solinteg} key={Inverter.Solinteg}>
          <FormattedMessage id="inverter.solinteg" defaultMessage="Solinteg" />
        </MenuItem>
        <MenuItem value={Inverter.Deye} key={Inverter.Deye}>
          <FormattedMessage id="inverter.deye" defaultMessage="Deye LV" />
        </MenuItem>
        <MenuItem value={Inverter.DeyeHV} key={Inverter.DeyeHV}>
          <FormattedMessage id="inverter.deye_hv" defaultMessage="Deye HV" />
        </MenuItem>
        <MenuItem value={Inverter.Wattsonic} key={Inverter.Wattsonic}>
          <FormattedMessage
            id="inverter.wattsonic"
            defaultMessage="Wattsonic"
          />
        </MenuItem>
        <MenuItem value={Inverter.Fronius} key={Inverter.Fronius}>
          <FormattedMessage
            id="inverter.fronius"
            defaultMessage="Fronius SYMO GEN24"
          />
        </MenuItem>
        <MenuItem value={Inverter.FroniusGrid} key={Inverter.FroniusGrid}>
          <FormattedMessage
            id="inverter.fronius_grid"
            defaultMessage="Fronius SYMO síťový"
          />
        </MenuItem>
        <MenuItem value={Inverter.SmaGrid} key={Inverter.SmaGrid}>
          <FormattedMessage
            id="inverter.sma_grid"
            defaultMessage="SMA síťový"
          />
        </MenuItem>
        <MenuItem value={Inverter.FroniusVerto} key={Inverter.FroniusVerto}>
          <FormattedMessage
            id="inverter.fronius_verto"
            defaultMessage="Fronius Verto"
          />
        </MenuItem>
        <MenuItem value={Inverter.SolarEdgeGrid} key={Inverter.SolarEdgeGrid}>
          <FormattedMessage
            id="inverter.solar_edge_grid"
            defaultMessage="SolarEdge síťový"
          />
        </MenuItem>
        <MenuItem value={Inverter.Sermatec} key={Inverter.Sermatec}>
          <FormattedMessage id="inverter.sermatec" defaultMessage="Sermatec" />
        </MenuItem>

        {boardRS485 && (
          <MenuItem
            value={Inverter.SofarSolarRS485}
            key={Inverter.SofarSolarRS485}
          >
            <FormattedMessage
              id="inverter.sofar_solar_rs485"
              defaultMessage="SofarSolar RS485"
            />
          </MenuItem>
        )}

        {boardRS485 && (
          <MenuItem
            value={Inverter.DesBatteryContainer}
            key={Inverter.DesBatteryContainer}
          >
            <FormattedMessage
              id="inverter.des_battery_container"
              defaultMessage="DES - kontejnerové úložiště"
            />
          </MenuItem>
        )}

        {boardRS485 && (
          <MenuItem value={Inverter.Hes} key={Inverter.Hes}>
            <FormattedMessage id="inverter.hes" defaultMessage="HES" />
          </MenuItem>
        )}

        <MenuItem value={Inverter.Next} key={Inverter.Next}>
          <FormattedMessage id="inverter.next" defaultMessage="Více střídačů" />
        </MenuItem>
        <MenuItem value={Inverter.None} key={Inverter.None}>
          <FormattedMessage id="inverter.none" defaultMessage="Žádný" />
        </MenuItem>
      </Select>
    </FormControl>
  );

  const connectionTypeValue = useSelector(
    ({
      variables: {
        PV_INVERTER_TYPE,
        MODBUS_TEST_ADDRESS,
        MODBUS_TEST_PORT,
        INVERTER_CONFIG,
        BOARD_RS485_HOST,
        BOARD_RS485_PORT,
      },
    }) => {
      if (PV_INVERTER_TYPE === Inverter.Next) {
        const inverterConfigArray = Object.values(INVERTER_CONFIG);

        if (!inverterConfigArray.length) {
          return ConnectionType.ModbusTCP;
        }

        const inverter = inverterConfigArray[0];

        if (
          inverter.type === Inverter.SofarSolarRS485 ||
          inverter.type === Inverter.Hes
        ) {
          return ConnectionType.RS485;
        }

        if (inverter.type === Inverter.Shellies) {
          return ConnectionType.ModbusTCP;
        }

        // eslint-disable-next-line
        // @ts-ignore
        return inverterConfigArray[0].address === BOARD_RS485_HOST &&
          // eslint-disable-next-line
          // @ts-ignore
          inverterConfigArray[0].port === BOARD_RS485_PORT
          ? ConnectionType.RS485
          : ConnectionType.ModbusTCP;
      } else {
        return MODBUS_TEST_ADDRESS === BOARD_RS485_HOST &&
          MODBUS_TEST_PORT === BOARD_RS485_PORT
          ? ConnectionType.RS485
          : ConnectionType.ModbusTCP;
      }
    }
  );

  const handleConnectionTypeChange = (e: SelectChangeEvent<ConnectionType>) => {
    const value = e.target.value as ConnectionType;

    if (value === ConnectionType.RS485 && inverterType !== Inverter.Next) {
      dispatch(
        variablesChange({
          MODBUS_TEST_ADDRESS: boardRS485Host,
          MODBUS_TEST_PORT: boardRS485Port,
        })
      );
    } else if (
      value === ConnectionType.ModbusTCP &&
      inverterType !== Inverter.Next
    ) {
      dispatch(variablesChange(defaultLegacyModbusConfig));
    } else if (
      value === ConnectionType.RS485 &&
      inverterType === Inverter.Next
    ) {
      const firstInverterKey = first(Object.keys(inverterConfig));
      dispatch(
        variablesChange({
          [`INVERTER_CONFIG.${firstInverterKey}.address`]: boardRS485Host,
          [`INVERTER_CONFIG.${firstInverterKey}.port`]: boardRS485Port,
        })
      );
    } else if (
      value === ConnectionType.ModbusTCP &&
      inverterType === Inverter.Next
    ) {
      const firstInverterKey = first(Object.keys(inverterConfig));
      dispatch(
        variablesChange({
          [`INVERTER_CONFIG.${firstInverterKey}.address`]: defaultModbusAddress,
          [`INVERTER_CONFIG.${firstInverterKey}.port`]: defaultModbusPort,
        })
      );
    }
  };

  const connectionTypeSelect = (
    <FormControl sx={{ width: 130 }}>
      <InputLabel id="connection-type-label">
        <FormattedMessage
          id="settings.service.connection_type"
          defaultMessage="Připojení"
        />
      </InputLabel>
      <Select
        value={connectionTypeValue}
        onChange={handleConnectionTypeChange}
        labelId="connection-type-label"
        label={
          <FormattedMessage
            id="settings.service.connection_type"
            defaultMessage="Připojení"
          />
        }
      >
        <MenuItem value={ConnectionType.ModbusTCP}>
          <FormattedMessage
            id="settings.service.modbus_tcp"
            defaultMessage="LAN/WiFi"
          />
        </MenuItem>
        <MenuItem value={ConnectionType.RS485} disabled={!rs485Supported}>
          <FormattedMessage
            id="settings.service.rs485"
            defaultMessage="RS485"
          />
        </MenuItem>
      </Select>
    </FormControl>
  );

  const legacyModbusAddressLabel = connectionTypeVisible
    ? connectionTypeValue === ConnectionType.ModbusTCP && (
        <Typography variant="subtitle2" sx={{ mt: 0.5, textAlign: 'right' }}>
          {legacyModbusAddress}
        </Typography>
      )
    : null;

  const wizardInverterInfo = useWizardInverterInfo();

  const wizardBox = (
    <Stack gap={0.75}>
      {inverterType !== Inverter.None && wizardInverterInfo}
      <Button
        variant={inverterType === Inverter.None ? 'contained' : 'outlined'}
        size="small"
        onClick={() => setWizardOpen(true)}
      >
        <FormattedMessage
          id="inverter_wizard.open"
          defaultMessage="Najít střídač"
        />
      </Button>
    </Stack>
  );

  const wizard = board === Board.Play || board === Board.V2 ? wizardBox : null;

  return (
    <>
      <ControlBox
        title={
          <FormattedMessage
            id="settings.docs.service_settings_inverter.title"
            defaultMessage="Návod na připojení střídače"
          />
        }
        subtitle={
          <FormattedMessage
            id="settings.docs.service_settings_inverter.subtitle"
            defaultMessage="Dokumentace, postupy a další informace"
          />
        }
        fullWidth
        control={
          <Box sx={{ pl: 0.25 }}>
            <Button
              variant="contained"
              href="https://infigy.cz/app-redirect/service-settings-inverter"
              target="_blank"
              sx={{ textWrap: 'nowrap' }}
              size="small"
            >
              <FormattedMessage
                id="settings.docs.service_settings_inverter.button"
                defaultMessage="Přejít na wiki"
              />
            </Button>
          </Box>
        }
      />

      <ControlBox
        control={
          wizard || (
            <Stack sx={{ my: -0.5 }}>
              <Row gap={0.5}>
                {inverterTypeSelect}
                {connectionTypeVisible && connectionTypeSelect}
              </Row>

              {legacyModbusAddressLabel}
            </Stack>
          )
        }
        title={
          <FormattedMessage
            id="settings.service.inverter_type"
            defaultMessage="Typ střídače"
          />
        }
        visibility={{ variableId: 'PV_INSTALL' }}
        fullWidth
      />

      <InverterWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </>
  );
};

const useWizardInverterInfo = () => {
  const [inverterType, legacyModbusAddress, inverterConfig] = useSelector(
    (s) =>
      [
        s.variables.PV_INVERTER_TYPE,
        s.variables.MODBUS_TEST_ADDRESS,
        s.variables.INVERTER_CONFIG,
      ] as const
  );

  if (inverterType !== Inverter.Next) {
    return (
      <>
        {inverterMapping[inverterType]}, {legacyModbusAddress}
      </>
    );
  }

  return (
    <Box sx={{ '& span:not(:last-child):after': { content: '", "' } }}>
      {Object.values(inverterConfig).map(({ type }, index) => {
        return <span key={index}>{inverterMapping[type]}</span>;
      })}
    </Box>
  );
};

export const inverterMapping: Record<Inverter, ReactNode> = {
  [Inverter.GoodWe]: (
    <FormattedMessage id="inverter.good_we" defaultMessage="GoodWe" />
  ),
  [Inverter.GoodWeNext]: (
    <FormattedMessage id="inverter.good_we_next" defaultMessage="GoodWe" />
  ),
  [Inverter.Solax]: (
    <FormattedMessage id="inverter.solax" defaultMessage="Solax" />
  ),
  [Inverter.SolaxNext]: (
    <FormattedMessage id="inverter.solax" defaultMessage="Solax" />
  ),
  [Inverter.SolaxEMS1000]: (
    <FormattedMessage
      id="inverter.solax_ems1000"
      defaultMessage="Solax EMS1000"
    />
  ),
  [Inverter.SolaxEMS1000PRO]: (
    <FormattedMessage
      id="inverter.solax_ems1000pro"
      defaultMessage="Solax EMS1000PRO"
    />
  ),
  [Inverter.Sofarsolar]: (
    <FormattedMessage id="inverter.sofar_solar" defaultMessage="Sofarsolar" />
  ),
  [Inverter.SolaxGrid]: (
    <FormattedMessage id="inverter.solax_grid" defaultMessage="Solax síťový" />
  ),
  [Inverter.SolaxMICGrid]: (
    <FormattedMessage
      id="inverter.solax_mic_grid"
      defaultMessage="Solax MIC síťový"
    />
  ),
  [Inverter.SolaxMEGAGrid]: (
    <FormattedMessage
      id="inverter.solax_mega_grid"
      defaultMessage="Solax MEGA/FORTH síťový"
    />
  ),
  [Inverter.GoodWeGrid]: (
    <FormattedMessage
      id="inverter.goodwe_grid"
      defaultMessage="Goodwe síťový"
    />
  ),
  [Inverter.GoodWeGridNext]: (
    <FormattedMessage
      id="inverter.good_we_grid_next"
      defaultMessage="GoodWeNext síťový"
    />
  ),
  [Inverter.Victron]: (
    <FormattedMessage id="inverter.victron" defaultMessage="Victron" />
  ),
  [Inverter.Solinteg]: (
    <FormattedMessage id="inverter.solinteg" defaultMessage="Solinteg" />
  ),
  [Inverter.Wattsonic]: (
    <FormattedMessage id="inverter.wattsonic" defaultMessage="Wattsonic" />
  ),
  [Inverter.SofarSolarRS485]: (
    <FormattedMessage
      id="inverter.sofar_solar_rs485"
      defaultMessage="SofarSolar RS485"
    />
  ),
  [Inverter.DesBatteryContainer]: (
    <FormattedMessage
      id="inverter.des_battery_container"
      defaultMessage="DES - kontejnerové úložiště"
    />
  ),
  [Inverter.Hes]: <FormattedMessage id="inverter.hes" defaultMessage="HES" />,
  [Inverter.Next]: (
    <FormattedMessage id="inverter.next" defaultMessage="Více střídačů" />
  ),
  [Inverter.None]: (
    <FormattedMessage id="inverter.none" defaultMessage="Žádný" />
  ),
  [Inverter.Shellies]: (
    <FormattedMessage id="inverter.shellies" defaultMessage="Shellies" />
  ),
  [Inverter.ShelliesExternal]: (
    <FormattedMessage
      id="inverter.shellies_external"
      defaultMessage="Shellies External"
    />
  ),
  [Inverter.ThreeMarPLC]: (
    <FormattedMessage id="inverter.3mar_plc" defaultMessage="3Mar PLC" />
  ),
  [Inverter.Sermatec]: (
    <FormattedMessage id="inverter.sermatec" defaultMessage="Sermatec" />
  ),
  [Inverter.FroniusGrid]: (
    <FormattedMessage
      id="inverter.fronius_grid"
      defaultMessage="Fronius SYMO síťový"
    />
  ),
  [Inverter.SmaGrid]: (
    <FormattedMessage id="inverter.sma_grid" defaultMessage="SMA síťový" />
  ),
  [Inverter.Fronius]: (
    <FormattedMessage
      id="inverter.fronius"
      defaultMessage="Fronius SYMO GEN24"
    />
  ),
  [Inverter.FroniusVerto]: (
    <FormattedMessage
      id="inverter.fronius_verto"
      defaultMessage="Fronius Verto"
    />
  ),
  [Inverter.Deye]: (
    <FormattedMessage id="inverter.deye" defaultMessage="Deye LV" />
  ),
  [Inverter.DeyeHV]: (
    <FormattedMessage id="inverter.deye_hv" defaultMessage="Deye HV" />
  ),
  [Inverter.SolarEdgeGrid]: (
    <FormattedMessage
      id="inverter.solar_edge_grid"
      defaultMessage="SolarEdge síťový"
    />
  ),
  [Inverter.AzRouter]: (
    <FormattedMessage
      id="inverter.az_router"
      defaultMessage="AzRouter elektroměr"
    />
  ),
};
